import { useState, useEffect } from 'react';
import { authFetch } from '@/utils/authFetch';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Calendar, FileText } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Reports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    tasks_completed: '',
    tasks_in_progress: '',
    blockers: '',
    notes: ''
  });

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await authFetch(`${BACKEND_URL}/api/reports`);

      if (response.ok) {
        const data = await response.json();
        setReports(data);
      }
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await authFetch(`${BACKEND_URL}/api/reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Report submitted successfully');
        setDialogOpen(false);
        setFormData({
          date: new Date().toISOString().split('T')[0],
          tasks_completed: '',
          tasks_in_progress: '',
          blockers: '',
          notes: ''
        });
        fetchReports();
      } else {
        toast.error('Failed to submit report');
      }
    } catch (error) {
      toast.error('Failed to submit report');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="reports-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Daily Work Reports</h1>
            <p className="text-slate-600 mt-1">Submit and view daily work reports</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="submit-report-button" className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="w-4 h-4 mr-2" />
                Submit Report
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="submit-report-dialog">
              <DialogHeader>
                <DialogTitle>Submit Daily Work Report</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="date">Date *</Label>
                  <Input
                    id="date"
                    type="date"
                    data-testid="report-date-input"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="tasks_completed">Tasks Completed *</Label>
                  <Textarea
                    id="tasks_completed"
                    data-testid="report-completed-input"
                    value={formData.tasks_completed}
                    onChange={(e) => setFormData({ ...formData, tasks_completed: e.target.value })}
                    required
                    placeholder="List tasks you completed today"
                    rows={3}
                  />
                </div>
                <div>
                  <Label htmlFor="tasks_in_progress">Tasks In Progress *</Label>
                  <Textarea
                    id="tasks_in_progress"
                    data-testid="report-inprogress-input"
                    value={formData.tasks_in_progress}
                    onChange={(e) => setFormData({ ...formData, tasks_in_progress: e.target.value })}
                    required
                    placeholder="List tasks currently in progress"
                    rows={3}
                  />
                </div>
                <div>
                  <Label htmlFor="blockers">Blockers (Optional)</Label>
                  <Textarea
                    id="blockers"
                    data-testid="report-blockers-input"
                    value={formData.blockers}
                    onChange={(e) => setFormData({ ...formData, blockers: e.target.value })}
                    placeholder="Any blockers or challenges faced"
                    rows={2}
                  />
                </div>
                <div>
                  <Label htmlFor="notes">Additional Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    data-testid="report-notes-input"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Any additional notes"
                    rows={2}
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" data-testid="submit-report-form-button">Submit Report</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Reports List */}
        <div className="grid grid-cols-1 gap-4">
          {reports.map((report) => (
            <Card key={report.report_id} className="card-hover" data-testid={`report-${report.report_id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center">
                    <Calendar className="w-5 h-5 mr-2 text-indigo-600" />
                    Report for {report.date}
                  </CardTitle>
                  <span className="text-sm text-slate-500">
                    {new Date(report.created_at).toLocaleDateString()}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-1">Tasks Completed</h4>
                  <p className="text-sm text-slate-600 whitespace-pre-wrap">{report.tasks_completed}</p>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-1">Tasks In Progress</h4>
                  <p className="text-sm text-slate-600 whitespace-pre-wrap">{report.tasks_in_progress}</p>
                </div>
                {report.blockers && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-1">Blockers</h4>
                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{report.blockers}</p>
                  </div>
                )}
                {report.notes && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-1">Additional Notes</h4>
                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{report.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
          {reports.length === 0 && (
            <Card>
              <CardContent className="py-12">
                <div className="text-center text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                  <p>No reports submitted yet</p>
                  <p className="text-sm mt-2">Click "Submit Report" to create your first report</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
}